import {
  Tr,
  Td,
  Card,
  Text,
  HStack,
  Checkbox,
  Box,
  Icon,
  useToast,
  Switch,
  useDisclosure,
  Tooltip,
  Spinner,
  Collapse,
  IconButton,
} from '@chakra-ui/react';
import { Invoice, invoiceReference } from '../types';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import { useContext, useEffect, useState } from 'react';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import { IoDownloadOutline, IoChevronDown } from 'react-icons/io5';
import MerchantContext from '../contexts/MerchantContext';
import PaymentDetailsContent from './PaymentDetailsContent';
import TooltipDateElement from './TooltipDateElement';
import { usePdfDownload } from '../hooks/usePdfDownload';
import { INVOICE_STATUS } from '../constants/enums';
import { useBreakpointsContext } from '../hooks/useBreakpointsContext';
import { DUE_DATE_TOOLTIP } from '../constants/texts';
import { useIsTouchDevice } from '../hooks/useIsTouchDevice';
import PaymentModalStatusContext from '../contexts/PaymentModalStatusContext';
import { useInvoices } from '../hooks/useInvoices';
import EditableAmount from './EditableAmount';

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('es-ES', { timeZone: 'UTC' });
};

const PAID_STATUS = 'Pagada';
const PARTIALLY_PAID_STATUS = 'Pago parcial';
const CANCELLED_STATUS = 'Cancelada';
const IN_PROCESS_STATUS = 'Procesando...';
const READY_STATUS = 'Por pagar';

type Props = {
  invoice: Invoice;
  tableIndex: number;
  showDetails?: boolean;
  showMinimumAmount?: boolean;
};

const TableElement = ({ invoice, tableIndex, showDetails, showMinimumAmount }: Props): JSX.Element => {
  const { isXLargeScreen, isDesktop } = useBreakpointsContext();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isDesktop && tableIndex === 0 });
  const isTouchDevice = useIsTouchDevice();
  const { readyInvoices } = useInvoices();
  const { invoicesToPay, addInvoiceToPay, removeInvoiceToPay, maxIndexSelected, invoiceSelectionLocked } =
    useContext(InvoicesToPayContext);
  const { setPartialAmount } = useContext(InvoicesToPayContext);
  const formatCurrency = useCurrencyFormatter();
  const { merchant } = useContext(MerchantContext);
  const toast = useToast();
  const [isMinimumAmount, setIsMinimumAmount] = useState(invoice.paymentIsMinimum ?? false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { setInvoiceIds: setPaymentModalInvoiceIds } = useContext(PaymentModalStatusContext);

  useEffect(() => {
    if (!merchant?.enforceOrderedPayments) return;

    const invoiceToPay = invoicesToPay.find((i) => i.id === invoice.id);
    if ((!invoiceToPay && tableIndex < maxIndexSelected) || (invoiceToPay && invoiceToPay.index !== tableIndex))
      addInvoiceToPay(invoice, isMinimumAmount, tableIndex);
  }, [invoicesToPay, tableIndex, addInvoiceToPay, invoice, isMinimumAmount, maxIndexSelected, merchant]);

  const isChecked = invoicesToPay.some((i) => i.id === invoice.id);

  const details = isMinimumAmount
    ? invoice.details?.filter((d: NonNullable<Invoice['details']>[number]) => d.isInMinimumPayment)
    : invoice.details;

  const date = invoice.status === INVOICE_STATUS.PAID ? formatDate(invoice.paymentDate!) : formatDate(invoice.dueDate);

  const status =
    invoice.status === INVOICE_STATUS.PAID
      ? invoice.partialPayment
        ? PARTIALLY_PAID_STATUS
        : PAID_STATUS
      : invoice.status === 'cancelled'
      ? CANCELLED_STATUS
      : invoice.paymentDate
      ? IN_PROCESS_STATUS
      : READY_STATUS;

  const partialAmount = invoicesToPay.find((i) => i.id === invoice.id)?.partialAmount;
  const canEditAmount =
    !!(merchant?.allowsPartialPayment || invoice.allowsPartialPayment) &&
    invoice.status === INVOICE_STATUS.READY &&
    !invoice.paymentDate &&
    !isMinimumAmount &&
    !invoiceSelectionLocked &&
    (!merchant?.enforceOrderedPayments || tableIndex === 0);

  const remainingAmount = partialAmount ? invoice.amount - partialAmount : invoice.partialPayment;

  const amount = invoice.paymentAmount
    ? formatCurrency(invoice.paymentAmount)
    : partialAmount
    ? formatCurrency(partialAmount)
    : isMinimumAmount && invoice.minimumAmount
    ? formatCurrency(invoice.minimumAmount)
    : formatCurrency(invoice.amount);

  const statusMessage =
    status === IN_PROCESS_STATUS
      ? invoice.paymentMethod === 'nequiButton'
        ? 'Aprueba tu pago en tu aplicación Nequi.'
        : invoice.paymentMethod === 'breb'
        ? 'Realiza el pago en tu aplicación de tu banco con Bre-B'
        : 'Tu pago puede tardar hasta 15 minutos.'
      : undefined;

  const statusColor =
    status === PAID_STATUS || status === PARTIALLY_PAID_STATUS
      ? 'rentals.states.approved'
      : status === CANCELLED_STATUS
      ? 'rentals.states.failed'
      : 'rentals.states.processing';

  const isDisabled =
    (!isChecked &&
      ((merchant?.enforceOrderedPayments && maxIndexSelected + 1 < tableIndex) ||
        invoice.status !== INVOICE_STATUS.READY ||
        !!invoice.paymentDate)) ||
    invoiceSelectionLocked;

  const onCheck = () => {
    if (isChecked) {
      removeInvoiceToPay(invoice, !!merchant?.enforceOrderedPayments);
    } else {
      addInvoiceToPay(invoice, isMinimumAmount, tableIndex);
    }
  };

  const onMinimumToggle = () => {
    setIsMinimumAmount(!isMinimumAmount);
    addInvoiceToPay(invoice, !isMinimumAmount, tableIndex);
  };

  const { downloadPdf } = usePdfDownload(invoice, merchant!, () => {
    toast({
      title: 'Esta factura no tiene un documento asociado',
      description: 'Si necesitas el documento, espera unos minutos o comunícate con nuestro equipo.',
    });
  });

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    await downloadPdf();
    setIsDownloading(false);
  };

  const DownloadButton = ({ text }: { text?: string }) => {
    return (
      <HStack
        cursor={isDownloading ? 'not-allowed' : 'pointer'}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDownloading) handleDownloadPdf();
        }}
        zIndex={100}
        bg="gray.100"
        _hover={isDownloading ? undefined : { bg: 'gray.200' }}
        borderRadius="full"
        px={text ? 3 : 2}
        py={1.5}
        transition="background 0.15s"
      >
        {text && (
          <Text fontWeight={500} fontSize={'0.875rem'}>
            {text}
          </Text>
        )}
        {isDownloading ? <Spinner size="sm" color="merchants.primary.app" /> : <Icon as={IoDownloadOutline} />}
      </HStack>
    );
  };

  const StatusText = ({ status }: { status: string }) => {
    const clickable = status === IN_PROCESS_STATUS && !invoiceSelectionLocked;
    return (
      <Tooltip hasArrow label={statusMessage} bg={'#606060'} p={2}>
        <Text
          color={statusColor}
          cursor={clickable ? 'pointer' : 'default'}
          onClick={(e) => {
            if (!clickable) return;
            e.stopPropagation();
            setPaymentModalInvoiceIds(
              invoice.paymentId ? readyInvoices.filter((i) => i.paymentId === invoice.paymentId).map((i) => i.id) : []
            );
          }}
        >
          {status}
        </Text>
      </Tooltip>
    );
  };

  const hasDetails = showDetails && !!invoice.details;

  const partialPaymentNotice = !!remainingAmount && (
    <Text fontSize={'0.8125rem'} color={'#F0873A'} bg={'#FFF4EC'} borderRadius={'md'} px={3} py={1.5}>
      Pago parcial: queda un saldo pendiente de {formatCurrency(remainingAmount)}
    </Text>
  );

  const RowElement = (
    <>
      {partialPaymentNotice && (
        <Tr>
          <Td colSpan={99} borderBottom={'none'} px={3} pt={3} pb={0}>
            {partialPaymentNotice}
          </Td>
        </Tr>
      )}
      <Tr>
        <Td borderBottom={hasDetails ? 'none' : '1px solid'} borderColor={'#DFDEE2'} px={3} py={4}>
          <Checkbox onChange={onCheck} isDisabled={isDisabled} isChecked={isChecked}>
            <Box width={'164px'} whiteSpace="normal">
              <Text>{invoice.description.replace('Primer pago', '')}</Text>
            </Box>
          </Checkbox>
        </Td>
        <Td borderBottom={hasDetails ? 'none' : '1px solid'} borderColor={'#DFDEE2'} px={3} py={4}>
          <Text>{invoiceReference(invoice)}</Text>
        </Td>
        <Td borderBottom={hasDetails ? 'none' : '1px solid'} borderColor={'#DFDEE2'} px={3} py={4}>
          <Text>{date}</Text>
        </Td>
        <Td borderBottom={hasDetails ? 'none' : '1px solid'} borderColor={'#DFDEE2'} px={3} py={4}>
          <EditableAmount
            isEditable={canEditAmount}
            amount={invoice.amount}
            partialAmount={partialAmount}
            onChange={(value) => setPartialAmount(invoice, tableIndex, value)}
          >
            <Tooltip
              hasArrow
              label={
                !merchant?.hidePaymentDeadlines &&
                !canEditAmount &&
                invoice.paymentDeadlines && <TooltipDateElement paymentDeadlines={invoice.paymentDeadlines} />
              }
              bg={'#606060'}
              p={2}
            >
              <Text>{amount}</Text>
            </Tooltip>
          </EditableAmount>
        </Td>
        <Td borderBottom={hasDetails ? 'none' : '1px solid'} borderColor={'#DFDEE2'} px={3} py={4}>
          <StatusText status={status} />
        </Td>
        {showMinimumAmount && (
          <Td borderBottom={hasDetails ? 'none' : '1px solid'} borderColor={'#DFDEE2'} px={3} py={4}>
            {invoice.minimumAmount ? (
              <Switch onChange={onMinimumToggle} isChecked={isMinimumAmount} isDisabled={isDisabled} />
            ) : (
              <Text color="gray.500"></Text>
            )}
          </Td>
        )}
        <Td borderBottom={hasDetails ? 'none' : '1px solid'} borderColor={'#DFDEE2'} px={3} py={4}>
          <Box display={'flex'} gap={2} justifyContent={'center'}>
            <DownloadButton />
          </Box>
        </Td>
        {showDetails && (
          <Td borderBottom={hasDetails ? 'none' : '1px solid'} borderColor={'#DFDEE2'} px={1} py={4}>
            {hasDetails && (
              <IconButton
                aria-label={isOpen ? 'Ocultar detalles' : 'Ver detalles'}
                icon={
                  <Icon
                    as={IoChevronDown}
                    transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="transform 0.2s"
                  />
                }
                variant="ghost"
                size="sm"
                borderRadius="full"
                bg="gray.100"
                _hover={{ bg: 'gray.200' }}
                onClick={onToggle}
              />
            )}
          </Td>
        )}
      </Tr>
      {hasDetails && (
        <Tr>
          <Td colSpan={99} borderBottom={'1px solid'} borderColor={'#DFDEE2'} px={3} py={0}>
            <Collapse in={isOpen} animateOpacity>
              <Box py={3}>
                <PaymentDetailsContent details={details || []} />
              </Box>
            </Collapse>
          </Td>
        </Tr>
      )}
    </>
  );

  const CardElement = (
    <Tr>
      <Td px={2.5}>
        <Card
          pb={6}
          pt={2}
          width={'100%'}
          onClick={isDisabled ? undefined : onCheck}
          boxShadow={isChecked ? '0 0 0 2px rgba(22,18,54,0.6), 0 6px 10px rgba(22,18,54,0.25)' : undefined}
        >
          {partialPaymentNotice && (
            <Box px={4} pt={2}>
              {partialPaymentNotice}
            </Box>
          )}
          <HStack
            justifyContent={'space-between'}
            alignItems="center"
            width={'100%'}
            borderBottom={'1px solid #DFDEE2'}
            py={4}
          >
            <Box cursor={isDisabled ? 'not-allowed' : 'pointer'}>
              <Checkbox
                fontWeight={700}
                pl={4}
                width={'auto'}
                isDisabled={isDisabled}
                isChecked={isChecked}
                pointerEvents="none"
              >
                <Text fontSize={'0.875rem'} whiteSpace="normal" overflowWrap="anywhere" noOfLines={4}>
                  {invoice.description.replace('Primer pago', '')}
                </Text>
              </Checkbox>
            </Box>
            <Box display={'flex'} gap={2} pr={4}>
              <DownloadButton text={merchant?.erp === 'mobilia' ? 'Cod. de Barras' : undefined} />
            </Box>
          </HStack>
          <Box px={4} gap={2}>
            <HStack justifyContent={'space-between'} py={2} borderBottom={'0.5px solid'} borderColor={'#DFDEE2'}>
              <Text fontWeight={600} fontSize={'0.875rem'}>
                N.Referencia
              </Text>
              <Box textAlign={'right'}>
                <Text fontSize={'0.875rem'} fontWeight={400}>
                  {invoiceReference(invoice)}
                </Text>
              </Box>
            </HStack>
            <HStack justifyContent={'space-between'} py={2} borderBottom={'0.5px solid'} borderColor={'#DFDEE2'}>
              <Box display={'flex'} flexDirection={'row'} gap={2} alignItems={'center'}>
                <Text fontWeight={600} fontSize={'0.875rem'}>
                  {invoice.status === INVOICE_STATUS.PAID ? 'Fecha de Pago' : 'Fecha de Vencimiento'}
                </Text>
                {!isTouchDevice && invoice.status === INVOICE_STATUS.READY && (
                  <Tooltip hasArrow label={DUE_DATE_TOOLTIP} bg={'#606060'} p={2}>
                    <Icon boxSize={3.5} />
                  </Tooltip>
                )}
              </Box>
              <Box textAlign={'right'}>
                <Text fontSize={'0.875rem'} fontWeight={400}>
                  {date}
                </Text>
              </Box>
            </HStack>
            <HStack justifyContent={'space-between'} py={2} borderBottom={'0.5px solid'} borderColor={'#DFDEE2'}>
              <Text fontWeight={600} fontSize={'0.875rem'}>
                {invoice.status === INVOICE_STATUS.PAID ? 'Valor Pagado' : 'Valor a Pagar'}
              </Text>
              <Box textAlign={'right'}>
                <EditableAmount
                  isEditable={canEditAmount}
                  amount={invoice.amount}
                  partialAmount={partialAmount}
                  onChange={(value) => setPartialAmount(invoice, tableIndex, value)}
                >
                  <Tooltip
                    hasArrow
                    label={
                      !merchant?.hidePaymentDeadlines &&
                      !canEditAmount &&
                      invoice.paymentDeadlines && <TooltipDateElement paymentDeadlines={invoice.paymentDeadlines} />
                    }
                    bg={'#606060'}
                    p={2}
                  >
                    <Text fontSize={'0.875rem'} fontWeight={400}>
                      {amount}
                    </Text>
                  </Tooltip>
                </EditableAmount>
              </Box>
            </HStack>
            <HStack justifyContent={'space-between'} py={2}>
              <Text fontWeight={600} fontSize={'0.875rem'}>
                Estado
              </Text>
              <Box textAlign={'right'}>
                <StatusText status={status} />
                {statusMessage && (
                  <Text fontSize={'0.75rem'} color={statusColor} maxW={'150px'} whiteSpace={'normal'}>
                    {statusMessage}
                  </Text>
                )}
              </Box>
            </HStack>
            {showMinimumAmount && invoice.minimumAmount && (
              <HStack py={2} borderTop={'1px solid #DFDEE2'} justifyContent={'space-between'}>
                <Text fontSize={'0.875rem'} fontWeight={600}>
                  Pago Mínimo
                </Text>
                <Switch onChange={onMinimumToggle} isChecked={isMinimumAmount} isDisabled={isDisabled} />
              </HStack>
            )}
            {showDetails && invoice.details && (
              <Box borderTop={'1px solid #DFDEE2'}>
                <HStack
                  py={3}
                  justifyContent={'center'}
                  cursor="pointer"
                  spacing={1.5}
                  opacity={0.7}
                  _hover={{ opacity: 1 }}
                  transition="opacity 0.15s"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle();
                  }}
                >
                  <Text fontSize={'0.8rem'} fontWeight={600} color="gray.600" letterSpacing="0.02em">
                    {isOpen ? 'Ocultar detalles' : 'Ver detalles'}
                  </Text>
                  <Icon
                    as={IoChevronDown}
                    color="gray.600"
                    boxSize={3.5}
                    transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="transform 0.2s"
                  />
                </HStack>
                <Collapse in={isOpen} animateOpacity>
                  <PaymentDetailsContent details={details || []} />
                </Collapse>
              </Box>
            )}
          </Box>
        </Card>
      </Td>
    </Tr>
  );

  return isXLargeScreen ? RowElement : CardElement;
};

export default TableElement;
